import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Chip,
  DialogContentText,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import StackedStaticDataDisplay from "common/StackedStaticDataDisplay";
import { DecisionAdditionalParagraph, FetchDecisionAdditionalParagraphsQuery } from "decisions";
import { Formik, Form as FormikForm, Field as FormikField } from "formik";
import { RadioGroup as FmuiRadioGroup, CheckboxWithLabel as FmuiCheckboxWithLabel, TextField as FmuiTextField } from "formik-mui";
import { makeStyles } from "makeStyles";
import React, { useRef, useState } from "react";
import { meetingStyles, staticDataStyles } from "styles/common";
import { useUnsavedChanges } from "UnsavedChangesProvider";
import { RegistrationCommitteeItem, RegistrationCommitteeItemDecision, RegistrationCommitteeItemDecisionInput } from ".";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { getHtmlForField } from "util/utilities";
import RichTextEditor from "common/RichTextEditor";
import { MergeFieldType } from "common/HtmlMergeFields/models";
import {
  DeleteFileFromRegistrationCommitteeItemMutation,
  FetchRegistrationCommitteeItemByIdQuery,
  SaveRegistrationCommitteeItemDecisionMutation,
  SaveRegistrationCommitteeItemDecisionNotesMutation,
  TableRegistrationCommitteeItemDecisionMutation,
} from "./queries";
import { useNotifications } from "notifications";
import ReadOnlyWrapper from "common/ReadOnlyWrapper";
import { Permissions, useCurrentUser } from "users";
import { NumberedTextFieldEndAdornment } from "common/NumberedTextfield";
import { DocType } from "common/DocType";
import ReplaceableDocumentLink from "common/ReplaceableDocumentLink";
import { AxiosResponse } from "axios";
import { useAppConfig } from "util/AppConfig";
import { useAxios } from "auth/SecureAxios";
import { ConfirmationDialog } from "common/ConfirmationDialog";
import { AvailableMeetingsQuery, CommitteeMeeting, FetchCommitteeMeetingToEnterDecisionsQuery } from "committee-meetings";
import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import PrsDatePickerField from "common/FormikFields/PrsDatePickerField";
import { FrequencyOptions } from "monitoring/models";
import { MicrosoftWordLink } from "common/MicrosoftWordLink";

const useStyles = makeStyles()((theme) => ({
  ...meetingStyles(theme),
  ...staticDataStyles(theme),
  independentHeight: {
    height: "fit-content",
  },
}));

interface Props {
  item: RegistrationCommitteeItem;
}

interface DecisionFormValues {
  id?: number;
  itemId: number;

  committeeDecision: "approve" | "defer" | "refuse" | "table" | null;

  additionalCommentsForMinutes: string | null;
  additionalCommentsForDecisionLetter: string | null;
  generalNotes: string | null;
  appendedParagraphNames: string[];

  monitoringRequired: boolean;
  monitoringFrequencyInDays: number | null;
  monitoringCount: number | null;
  monitoringSingleHardDeadlineDate: DateTime | null;
  writtenDecisionRequired: boolean;
}

const RegistrationCommitteeItemDecisionTab: React.FunctionComponent<Props> = (props) => {
  const { classes } = useStyles();
  const notifications = useNotifications();
  const history = useHistory();
  const user = useCurrentUser();
  const appConfig = useAppConfig();
  const { secureAxios } = useAxios();
  const apolloClient = useApolloClient();
  const { unsavedChanges, changesSaved, setSaveFunction } = useUnsavedChanges();

  const [displayNoVerbiageWarning, setDisplayNoVerbiageWarning] = useState(false);
  const [displayTableConfirmation, setDisplayTableConfirmation] = useState(false);
  const [displayUntablePprpConfirmation, setDisplayUntablePprpConfirmation] = useState(false);

  const additionalParagraphsQuery = useQuery<
    { decisionAdditionalParagraphs: DecisionAdditionalParagraph[] },
    { forRegistrationCommitteeItems: boolean; activeOnly: boolean }
  >(FetchDecisionAdditionalParagraphsQuery, { variables: { forRegistrationCommitteeItems: true, activeOnly: true } });
  const additionalParagraphs = additionalParagraphsQuery.data?.decisionAdditionalParagraphs ?? [];

  const [selectedAdditionalParagraphId, setSelectedAdditionalParagraphId] = useState<number | null>(null);

  const additionalCommentsForMinutesContentRetriever = useRef<{ getContentAsHtml: () => string | null }>({ getContentAsHtml: () => null });
  const additionalCommentsForDecisionLetterContentRetriever = useRef<{ getContentAsHtml: () => string | null }>({
    getContentAsHtml: () => null,
  });
  const additionalCommentsForGeneralNotesRetriever = useRef<{ getContentAsHtml: () => string | null }>({ getContentAsHtml: () => null });

  const [legalCounselToAttach, setLegalCounselToAttach] = useState<File | undefined>();
  const [removingLegalCounsel, setRemovingLegalCounsel] = useState(false);

  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);

  const [deleteFile] = useMutation<
    { registrationCommitteeItem: { deleteFile: RegistrationCommitteeItem } },
    { itemId: number; attachedDocumentId: number }
  >(DeleteFileFromRegistrationCommitteeItemMutation);

  const [saveMutate] = useMutation<
    { registrationCommiteeItem: { saveDecision: RegistrationCommitteeItemDecision } },
    { decisionInput: RegistrationCommitteeItemDecisionInput }
  >(SaveRegistrationCommitteeItemDecisionMutation, {
    refetchQueries: [
      {
        query: FetchCommitteeMeetingToEnterDecisionsQuery,
        variables: {
          meetingId: props.item.committeeMeeting?.id ?? 0,
          meetingDate: props.item.committeeMeeting?.meetingDate ?? DateTime.now().toISODate(),
        },
      },
    ],
  });

  const [saveDecisionNotesMutate] = useMutation<
    { registrationCommitteeItem: { saveDecisionNotes: RegistrationCommitteeItemDecision } },
    { itemId: number; generalNotes: string | null }
  >(SaveRegistrationCommitteeItemDecisionNotesMutation);

  const [tableMutate, { loading: tableMutateLoading }] = useMutation<
    { registrationCommitteeItem: { tableDecision: RegistrationCommitteeItem } },
    { decisionInput: RegistrationCommitteeItemDecisionInput; meetingId: number }
  >(TableRegistrationCommitteeItemDecisionMutation, {
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data?.registrationCommitteeItem?.tableDecision?.id) {
        notifications.success("Decision tabled successfully.");
        changesSaved();
        setDisplayTableConfirmation(false);

        history.push(`/registration-committee-item/${data.registrationCommitteeItem.tableDecision.id}/overview`);
      }
    },
    onError: () => {
      setDisplayTableConfirmation(false);
    },
    refetchQueries: [
      {
        query: FetchRegistrationCommitteeItemByIdQuery,
        variables: { id: props.item.id },
      },
    ],
  });

  const meetingsQuery = useQuery<{ availableMeetings: CommitteeMeeting[] }>(AvailableMeetingsQuery);
  const availableMeetings = meetingsQuery.data?.availableMeetings.filter((m) => m.isRegistrationCommittee) ?? [];

  const decision = props.item.decision;
  const userCanEditDecision = user.userHasPermission(Permissions.AdminTasksPprp) && !props.item.isCompleted;
  const userCanEditDecisionNotes =
    user.userHasPermission(Permissions.AdminTasksPprp) || user.userHasPermission(Permissions.RegComItemEditDecisionNotes);
  const getCommitteeDecision = (decision: RegistrationCommitteeItemDecision | null) => {
    if (!decision) return null;
    if (decision.isApproved) return "approve";
    if (decision.isDeferred) return "defer";
    if (decision.isRefused) return "refuse";
    if (decision.isTabled) return "table";
    return null;
  };

  const initialValues: DecisionFormValues = {
    id: decision?.id,
    itemId: props.item.id,

    committeeDecision: getCommitteeDecision(decision),

    additionalCommentsForMinutes: decision?.additionalCommentsForMinutes ?? null,
    additionalCommentsForDecisionLetter: decision?.additionalCommentsForDecisionLetter ?? null,
    generalNotes: decision?.generalNotes ?? null,
    appendedParagraphNames: decision?.appendedParagraphNames ?? [],
    monitoringRequired: decision?.monitoringRequired ?? false,
    monitoringFrequencyInDays: decision?.monitoringSingleHardDeadlineDate
      ? FrequencyOptions.HardDeadline.value
      : (decision?.monitoringFrequencyInDays ?? 365),
    monitoringCount: decision?.monitoringCount ?? 1,

    writtenDecisionRequired: decision?.writtenDecisionRequired ?? false,
    monitoringSingleHardDeadlineDate: decision?.monitoringSingleHardDeadlineDate
      ? DateTime.fromISO(decision.monitoringSingleHardDeadlineDate)
      : null,
  };

  const updateRegistrationCommitteeItemInCache = (updatedItem: RegistrationCommitteeItem) => {
    const cacheId = `RegistrationCommitteeItem:${updatedItem.id}`;

    apolloClient.cache.modify({
      id: cacheId,
      fields: {
        attachedDocuments() {
          return updatedItem.attachedDocuments;
        },
      },
    });
  };

  const createDecisionInput = (
    decision: RegistrationCommitteeItemDecision | null,
    itemId: number,
    values: DecisionFormValues,
    additionalCommentsForMinutesRef: React.MutableRefObject<{ getContentAsHtml: () => string | null }>,
    additionalCommentsForDecisionLetterRef: React.MutableRefObject<{ getContentAsHtml: () => string | null }>,
    generalNotesRef: React.MutableRefObject<{ getContentAsHtml: () => string | null }>,
  ): RegistrationCommitteeItemDecisionInput => {
    const isHardDeadline = () => values.monitoringFrequencyInDays === FrequencyOptions.HardDeadline.value;
    const hasMonitoring = () => values.monitoringRequired;
    const getMonitoringValue = (condition: boolean, value: any) => (hasMonitoring() && condition ? value : null);

    return {
      id: decision?.id,
      registrationCommitteeItemId: itemId,
      isApproved: values.committeeDecision === "approve",
      isDeferred: values.committeeDecision === "defer",
      isRefused: values.committeeDecision === "refuse",
      isTabled: values.committeeDecision === "table",
      additionalCommentsForMinutes: additionalCommentsForMinutesRef.current.getContentAsHtml(),
      additionalCommentsForDecisionLetter: additionalCommentsForDecisionLetterRef.current.getContentAsHtml(),
      generalNotes: generalNotesRef.current.getContentAsHtml(),
      appendedParagraphNames: values.appendedParagraphNames,
      monitoringRequired: hasMonitoring(),
      monitoringFrequencyInDays: getMonitoringValue(!isHardDeadline(), values.monitoringFrequencyInDays),
      monitoringCount: getMonitoringValue(!isHardDeadline(), Number(values.monitoringCount)),
      monitoringSingleHardDeadlineDate: getMonitoringValue(
        isHardDeadline() && values.monitoringSingleHardDeadlineDate != null,
        values.monitoringSingleHardDeadlineDate?.toISODate(),
      ),
      writtenDecisionRequired: values.writtenDecisionRequired,
    };
  };

  async function saveDecision(values: DecisionFormValues) {
    if (legalCounselToAttach) {
      const formData = new FormData();
      formData.append("RegistrationCommitteeItemId", props.item.id.toString());
      formData.append("Document", legalCounselToAttach);

      let postResult: AxiosResponse<RegistrationCommitteeItem>;
      const postRequestEndpoint = `${appConfig.apiEndpoint}/api/registration-committee-item-document/upload-legal-counsel-verbiage`;
      try {
        postResult = await secureAxios.post(postRequestEndpoint, formData);
        if (postResult === undefined || postResult?.status === 401) {
          postResult = await secureAxios.post(postRequestEndpoint, formData);
        }
      } catch (e: any) {
        notifications.serverError(e.message);
        return;
      }

      const updatedItem = postResult.data;
      updateRegistrationCommitteeItemInCache(updatedItem);
      setLegalCounselToAttach(undefined);
    }
    const input = createDecisionInput(
      decision,
      props.item.id,
      values,
      additionalCommentsForMinutesContentRetriever,
      additionalCommentsForDecisionLetterContentRetriever,
      additionalCommentsForGeneralNotesRetriever,
    );
    var result = await saveMutate({ variables: { decisionInput: input } });
    if (result.data) {
      notifications.success("Decision saved.");
      changesSaved();
      setDisplayNoVerbiageWarning(false);
      setDisplayUntablePprpConfirmation(false);
    }
  }

  async function saveDecisionNotesOnly(values: DecisionFormValues) {
    const generalNotes = additionalCommentsForGeneralNotesRetriever.current.getContentAsHtml();

    var result = await saveDecisionNotesMutate({
      variables: {
        itemId: props.item.id,
        generalNotes: generalNotes,
      },
    });

    if (result.data) {
      notifications.success("Decision notes saved.");
      changesSaved();
    }
  }

  const handleTableConfirmation = async (formikProps: any) => {
    const input = createDecisionInput(
      decision,
      props.item.id,
      formikProps.values,
      additionalCommentsForMinutesContentRetriever,
      additionalCommentsForDecisionLetterContentRetriever,
      additionalCommentsForGeneralNotesRetriever,
    );

    await tableMutate({
      variables: {
        decisionInput: input,
        meetingId: selectedMeetingId ?? 0, // Default to 0 if null
      },
    });
  };

  const decisionLetter = props.item?.attachedDocuments?.filter((ad) => ad.type === DocType.RegistrationCommitteeItemDecisionLetter)?.[0];

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, helpers) => {
        // If the item is already completed, only save the decision notes
        if (props.item.isCompleted) {
          if (userCanEditDecisionNotes) {
            await saveDecisionNotesOnly(values);
          }
          return;
        }

        // If the item is already tabled, only save the decision
        if (props.item.decision?.isTabled && values.committeeDecision === "table") {
          await saveDecision(values);
          return;
        }

        // If the decision is being UN-tabled and it's a PPRP RCI, make them confirm it
        if (
          props.item.decision?.isTabled &&
          values.committeeDecision !== "table" &&
          props.item.pprpProgramReviewId &&
          props.item.tabledToItemId
        ) {
          setDisplayUntablePprpConfirmation(true);
          helpers.setSubmitting(false);
          return;
        }

        // If the decision is being newly tabled
        if (values.committeeDecision === "table") {
          setDisplayTableConfirmation(true);
          helpers.setSubmitting(false);
        } else if (
          (values.committeeDecision === "defer" || values.committeeDecision === "refuse") &&
          !(
            legalCounselToAttach ||
            props.item.attachedDocuments.some((ad) => ad.type === DocType.RegistrationCommitteeLegalCounselDecisionVerbiage)
          )
        ) {
          setDisplayNoVerbiageWarning(true);
          helpers.setSubmitting(false);
        } else {
          await saveDecision(values);
        }
      }}
    >
      {(formikProps) => {
        setSaveFunction(async () => {
          await formikProps.submitForm();
          return true;
        });
        return (
          <FormikForm onChange={() => unsavedChanges()}>
            <Grid container columnSpacing={3} sx={{ height: "100%" }}>
              <Grid container item xs={10} rowSpacing={2} className={classes.independentHeight}>
                <Grid item xs={12}>
                  <StackedStaticDataDisplay label="Committee Meeting" value={props.item.committeeMeeting?.name ?? "--"} />
                </Grid>
                <Grid container item columnSpacing={2} xs={12}>
                  <Grid item xs={2}>
                    <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
                      <ReadOnlyWrapper
                        readOnly={!userCanEditDecision}
                        title={
                          props.item.isCompleted ? "The item has been completed." : "Only the registrar may edit the committee decision."
                        }
                      >
                        <FormikField
                          component={FmuiCheckboxWithLabel}
                          type="checkbox"
                          name="monitoringRequired"
                          Label={{ label: "Monitoring Required?" }}
                          disabled={formikProps.isSubmitting || !userCanEditDecision}
                        />
                      </ReadOnlyWrapper>
                    </div>
                  </Grid>
                  {formikProps.values.monitoringRequired && (
                    <>
                      <Grid item xs={2}>
                        <ReadOnlyWrapper
                          readOnly={!userCanEditDecision}
                          title={
                            props.item.isCompleted ? "The item has been completed." : "Only the registrar may edit the committee decision."
                          }
                        >
                          <FormikField
                            component={FmuiTextField}
                            disabled={formikProps.isSubmitting || !userCanEditDecision}
                            select
                            name="monitoringFrequencyInDays"
                            label="Frequency"
                            fullWidth
                          >
                            {Object.entries(FrequencyOptions).map(([key, { value, label }]) => (
                              <MenuItem key={key} value={value}>
                                {label}
                              </MenuItem>
                            ))}
                          </FormikField>
                        </ReadOnlyWrapper>
                      </Grid>
                      <Grid item xs={2}>
                        <ReadOnlyWrapper
                          readOnly={!userCanEditDecision}
                          title={
                            props.item.isCompleted ? "The item has been completed." : "Only the registrar may edit the committee decision."
                          }
                        >
                          {formikProps.values.monitoringFrequencyInDays === FrequencyOptions.HardDeadline.value ? (
                            <FormikField
                              component={PrsDatePickerField}
                              name="monitoringSingleHardDeadlineDate"
                              label="Deadline Date"
                              fullWidth
                              minDate={DateTime.local()}
                              disabled={formikProps.isSubmitting || !userCanEditDecision}
                            />
                          ) : (
                            <FormikField
                              component={FmuiTextField}
                              name="monitoringCount"
                              label="Number of Occurrences"
                              fullWidth
                              required
                              InputProps={{
                                inputProps: { min: 1 },
                                endAdornment: (
                                  <NumberedTextFieldEndAdornment
                                    value={formikProps.values.monitoringCount ?? 0}
                                    onChange={(newValue) => formikProps.setFieldValue("monitoringCount", newValue)}
                                    min={1}
                                  />
                                ),
                              }}
                              disabled={formikProps.isSubmitting || !userCanEditDecision}
                            />
                          )}
                        </ReadOnlyWrapper>
                      </Grid>
                    </>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="h4" sx={{ mb: 0, mr: 3, alignSelf: "center" }}>
                      Paragraphs
                    </Typography>
                    <TextField
                      label="Paragraph to Append"
                      select
                      value={selectedAdditionalParagraphId}
                      onChange={(e) => setSelectedAdditionalParagraphId(Number(e.target.value))}
                      sx={{ flex: 1 }}
                      disabled={formikProps.isSubmitting || !userCanEditDecision}
                    >
                      {_.orderBy(additionalParagraphs, (ap) => ap.name).map((ap) => (
                        <MenuItem value={ap.id} key={ap.id}>
                          {ap.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="outlined"
                      disabled={formikProps.isSubmitting || !userCanEditDecision || !selectedAdditionalParagraphId}
                      onClick={() => {
                        const additionalParagraphToAppend = additionalParagraphs.find((ac) => ac.id === selectedAdditionalParagraphId)!;

                        formikProps.setFieldValue("appendedParagraphNames", [
                          ...formikProps.values.appendedParagraphNames,
                          additionalParagraphToAppend.name,
                        ]);

                        const currentHtmlForMinutes = getHtmlForField(additionalCommentsForMinutesContentRetriever);
                        const updatedHtmlForMinutes = (currentHtmlForMinutes ?? "") + additionalParagraphToAppend.paragraphForMinutes;
                        formikProps.setFieldValue("additionalCommentsForMinutes", updatedHtmlForMinutes);

                        const currentHtmlForDecisionLetter = getHtmlForField(additionalCommentsForDecisionLetterContentRetriever);
                        const updatedHtmlForDecisionLetter =
                          (currentHtmlForDecisionLetter ?? "") + additionalParagraphToAppend.paragraphForDecisionLetter;
                        formikProps.setFieldValue("additionalCommentsForDecisionLetter", updatedHtmlForDecisionLetter);
                      }}
                    >
                      Append
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  {formikProps.values.appendedParagraphNames.length > 0 && (
                    <Stack spacing={1}>
                      <Typography variant="body2" gutterBottom color="text.secondary">
                        Selected Paragraphs:
                      </Typography>
                      <Stack
                        direction="column"
                        spacing={1}
                        sx={{
                          alignItems: "flex-start",
                        }}
                      >
                        {formikProps.values.appendedParagraphNames.map((paragraphName, index) => {
                          return (
                            <Chip
                              key={index}
                              label={additionalParagraphsQuery.loading ? <Skeleton width={80} height={16} /> : paragraphName}
                              onDelete={() => {
                                // Remove by index instead of filtering by name (allowing multiple paragraphs with the same name)
                                const updatedNames = formikProps.values.appendedParagraphNames.filter((_, i) => i !== index);
                                formikProps.setFieldValue("appendedParagraphNames", updatedNames);
                              }}
                              disabled={formikProps.isSubmitting || !userCanEditDecision || additionalParagraphsQuery.loading}
                              variant="outlined"
                            />
                          );
                        })}
                      </Stack>
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <RichTextEditor
                    label="Contents for Minutes"
                    minHeight="6em"
                    html={formikProps.values.additionalCommentsForMinutes}
                    passContentRetriever={(getContentAsHtml) => {
                      additionalCommentsForMinutesContentRetriever.current = { getContentAsHtml };
                    }}
                    reportUnsavedChanges
                    showMergeFields
                    mergeFieldType={MergeFieldType.RegComMeetingItem}
                    templateMarkup
                    readOnly={formikProps.isSubmitting || !userCanEditDecision}
                  />
                </Grid>
                <Grid item xs={12}>
                  {decisionLetter ? (
                    <>
                      <Typography variant="body1" component="div" className={classes.label}>
                        Contents for Decision Letter
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 0.5, mb: 1.5 }}>
                        <em>
                          Open the <MicrosoftWordLink href={decisionLetter.url}>Decision Letter</MicrosoftWordLink> document to edit its
                          contents directly.
                        </em>
                      </Typography>
                    </>
                  ) : (
                    <RichTextEditor
                      label="Contents for Decision Letter"
                      minHeight="6em"
                      html={formikProps.values.additionalCommentsForDecisionLetter}
                      passContentRetriever={(getContentAsHtml) => {
                        additionalCommentsForDecisionLetterContentRetriever.current = { getContentAsHtml };
                      }}
                      reportUnsavedChanges
                      showMergeFields
                      mergeFieldType={MergeFieldType.RegComItem}
                      templateMarkup
                      readOnly={formikProps.isSubmitting || !userCanEditDecision}
                    />
                  )}
                </Grid>

                <Grid item xs={12} my={1}>
                  <Typography variant="h3" mb={1}>
                    Written Decision
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <ReplaceableDocumentLink
                      documents={props.item.attachedDocuments}
                      documentName="Written Decision"
                      documentType={DocType.RegistrationCommitteeLegalCounselDecisionVerbiage}
                      documentToAttach={legalCounselToAttach}
                      attachDocument={setLegalCounselToAttach}
                      clearAttachedDocument={() => setLegalCounselToAttach(undefined)}
                      removeDocument={() => setRemovingLegalCounsel(true)}
                      acceptableFileTypes=".doc,.docx,.pdf"
                      disabled={formikProps.isSubmitting || !userCanEditDecision}
                    />
                    <FormikField
                      component={FmuiCheckboxWithLabel}
                      type="checkbox"
                      name="writtenDecisionRequired"
                      Label={{ label: "Written Decision Required?" }}
                      disabled={formikProps.isSubmitting || !userCanEditDecision}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <ReadOnlyWrapper
                    readOnly={!userCanEditDecisionNotes}
                    title={props.item.isCompleted ? "The item has been completed." : ""}
                  >
                    <RichTextEditor
                      label="General Notes"
                      minHeight="6em"
                      html={formikProps.values.generalNotes}
                      passContentRetriever={(getContentAsHtml) => {
                        additionalCommentsForGeneralNotesRetriever.current = { getContentAsHtml };
                      }}
                      reportUnsavedChanges
                      readOnly={formikProps.isSubmitting || !userCanEditDecisionNotes}
                    />
                  </ReadOnlyWrapper>
                </Grid>
              </Grid>
              <Grid item xs={2}>
                <Stack spacing={2} className={classes.independentHeight}>
                  <LoadingButton
                    loading={formikProps.isSubmitting}
                    color="primary"
                    variant="contained"
                    onClick={formikProps.submitForm}
                    sx={{ pt: 1.5, pb: 1.5 }}
                  >
                    Save
                  </LoadingButton>
                  <span>
                    <Typography variant="h3">Committee Decision</Typography>
                    <ReadOnlyWrapper
                      readOnly={!userCanEditDecision}
                      title={
                        props.item.isCompleted ? "The item has been completed." : "Only the registrar may edit the committee decision."
                      }
                    >
                      <FormikField component={FmuiRadioGroup} name="committeeDecision" sx={{ pl: 2 }} disabled={!userCanEditDecision}>
                        <FormControlLabel
                          label="Approve"
                          value="approve"
                          control={<Radio disabled={formikProps.isSubmitting} />}
                          disabled={formikProps.isSubmitting || !userCanEditDecision}
                        />
                        <FormControlLabel
                          label="Defer"
                          value="defer"
                          control={<Radio disabled={formikProps.isSubmitting} />}
                          disabled={formikProps.isSubmitting || !userCanEditDecision}
                        />
                        <FormControlLabel
                          label="Refuse"
                          value="refuse"
                          control={<Radio disabled={formikProps.isSubmitting} />}
                          disabled={formikProps.isSubmitting || !userCanEditDecision}
                        />
                        <FormControlLabel
                          label="Table"
                          value="table"
                          control={<Radio disabled={formikProps.isSubmitting} />}
                          disabled={formikProps.isSubmitting || !userCanEditDecision}
                        />
                      </FormikField>
                    </ReadOnlyWrapper>
                  </span>
                </Stack>
              </Grid>
            </Grid>

            {removingLegalCounsel && (
              <ConfirmationDialog
                open={true}
                title="Remove Written Decision"
                body={<DialogContentText>Are you sure you want to remove the Written Decision?</DialogContentText>}
                cancel={() => setRemovingLegalCounsel(false)}
                loading={formikProps.isSubmitting}
                confirm={async () => {
                  formikProps.setSubmitting(true);
                  await deleteFile({
                    variables: {
                      itemId: props.item.id,
                      attachedDocumentId: props.item.attachedDocuments.filter(
                        (ad) => ad.type === DocType.RegistrationCommitteeLegalCounselDecisionVerbiage,
                      )[0]!.id,
                    },
                  });
                  formikProps.setSubmitting(false);
                  setRemovingLegalCounsel(false);
                }}
              />
            )}
            {displayNoVerbiageWarning && (
              <ConfirmationDialog
                open={true}
                title="Save Without Written Decision?"
                body={
                  <DialogContentText>Are you sure you want to save the decision without uploading any Written Decision?</DialogContentText>
                }
                cancel={() => setDisplayNoVerbiageWarning(false)}
                loading={formikProps.isSubmitting}
                confirm={async () => await saveDecision(formikProps.values)}
              />
            )}
            {displayTableConfirmation && (
              <ConfirmationDialog
                open={true}
                title="Table Decision?"
                body={
                  <Stack spacing={3} my={1}>
                    <DialogContentText>
                      Are you sure you want to table this decision? Please select a committee meeting where the tabled item will be
                      considered in the future.
                    </DialogContentText>
                    <TextField
                      select
                      label="Select Committee Meeting"
                      value={selectedMeetingId || ""}
                      onChange={(e) => setSelectedMeetingId(Number(e.target.value))}
                      fullWidth
                      required
                      sx={{ mt: 2 }}
                    >
                      {availableMeetings.map((meeting) => (
                        <MenuItem key={meeting.id} value={meeting.id}>
                          {meeting.name} - {DateTime.fromISO(meeting.meetingDate).toLocaleString(DateTime.DATE_MED)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                }
                cancel={() => {
                  setDisplayTableConfirmation(false);
                  setSelectedMeetingId(null);
                }}
                loading={tableMutateLoading}
                confirm={() => (selectedMeetingId ? handleTableConfirmation(formikProps) : null)}
                confirmButtonDisabled={!selectedMeetingId}
              />
            )}
            {displayUntablePprpConfirmation && (
              <ConfirmationDialog
                open={true}
                title="Un-table PPRP Decision?"
                body={
                  <Stack spacing={3} my={1}>
                    <DialogContentText>This item was previously tabled to a subsequent meeting.</DialogContentText>
                    <DialogContentText>
                      This created a new item (#{props.item.tabledToItemId}) on that meeting, and updated the meeting of the associated PPRP
                      Program Review
                      {props.item.pprpProgramReview?.prNumber ? ` (#${props.item.pprpProgramReview?.prNumber})` : ""} accordingly.
                    </DialogContentText>
                    <DialogContentText>
                      Changing the decision on this item (#{props.item.id}) to no longer be tabled will have the following effects:
                      <ul>
                        <li>
                          PR {props.item.pprpProgramReview?.prNumber}'s meeting will change back to <i>this</i> item's meeting (
                          {props.item.committeeMeeting?.name}), and
                        </li>
                        <li>
                          the other item (#{props.item.tabledToItemId}) will no longer be connected to PR{" "}
                          {props.item.pprpProgramReview?.prNumber}.
                        </li>
                      </ul>
                      (The item will continue to appear on the other meeting, so that you can assess it for any important orphaned content
                      before manually deleting it, but the PR will not.)
                    </DialogContentText>
                    <DialogContentText>Are you sure you wish to do this?</DialogContentText>
                  </Stack>
                }
                cancel={() => {
                  setDisplayUntablePprpConfirmation(false);
                }}
                loading={formikProps.isSubmitting}
                confirm={async () => await saveDecision(formikProps.values)}
              />
            )}
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export default RegistrationCommitteeItemDecisionTab;
